<template>
  <div class="job-condition-container">
    <div v-rect-loading="loading" class="condition-container">
      <div class="condition location">
        <div class="condition-label">
          <span class="label">{{ $t(`${i18nPrefix}.label.aiTalentPoolRecommenderLocationLabel`) }}</span>
          <i v-auto-tooltip.always="$t(`${i18nPrefix}.label.aiTalentPoolRecommenderLocationTip`)" class="tip-icon el-icon-question"></i>
        </div>
        <div class="condition-controller">
          <RemoteLoadSelect
            v-model="condition.location"
            ref="location"
            labelKey="locationName"
            clearable
            multiple
            onlyFilter
            :disabled="onlyDisplay"
            :loader="loadLocation"
          ></RemoteLoadSelect>
        </div>
      </div>
      <div class="condition work-year">
        <div class="condition-label">
          {{ $t(`${i18nPrefix}.label.workYear`) }}
        </div>
        <div class="condition-controller">
          <RangeSelect
            v-model="condition.workYear"
            clearable
            :disabled="onlyDisplay"
            :optionList="workYearOptionList"
          ></RangeSelect>
        </div>
      </div>
      <div class="condition degree">
        <div class="condition-label">
          {{ $t(`${i18nPrefix}.label.degree`) }}
        </div>
        <div class="condition-controller">
          <el-select
            v-model="condition.degree"
            filterable
            multiple
            clearable
            :disabled="onlyDisplay"
          >
            <el-option
              v-for="(degree, key) in DEGREE"
              :key="key"
              :label="$t(`meta.DEGREE.${key}`)"
              :value="degree"
            ></el-option>
          </el-select>
        </div>
      </div>
      <div v-if="isTalentPool" class="condition activeness-type">
        <div class="condition-label">
          {{ $t(`${i18nPrefix}.label.activeness`) }}
        </div>
        <div class="condition-controller">
          <el-select
            v-model="condition.activenessType"
            clearable
            filterable
            multiple
            :disabled="onlyDisplay"
          >
            <el-option
              v-for="(activenessType, key) in ACTIVENESS"
              :key="key"
              :label="$t(`${i18nPrefix}.activenessType.${key}`)"
              :value="activenessType"
            ></el-option>
          </el-select>
        </div>
      </div>
      <div class="condition recent-industry">
        <div class="condition-label">
          {{ $t(`${i18nPrefix}.label.talentRecommendation.recentIndustry`) }}
        </div>
        <div class="condition-controller">
          <RemoteLoadSelect
            v-model="condition.recentIndustry"
            ref="recentIndustry"
            labelKey="i18nName"
            clearable
            multiple
            :disabled="onlyDisplay"
            :loader="loadIndustry"
          ></RemoteLoadSelect>
        </div>
      </div>
      <div class="condition skill">
        <div class="condition-label">
          {{ $t(`${i18nPrefix}.label.talentRecommendation.skills`) }}
        </div>
        <div class="condition-controller">
          <RemoteLoadSelect
            v-model="condition.tag"
            ref="tag"
            labelKey="i18nName"
            clearable
            multiple
            :disabled="onlyDisplay"
            :loader="loadTag"
          ></RemoteLoadSelect>
        </div>
      </div>
      <div class="condition industry">
        <div class="condition-label">
          {{ $t(`${i18nPrefix}.label.industry`) }}
        </div>
        <div class="condition-controller">
          <el-cascader
            v-model="condition.industry"
            clearable
            filterable
            :placeholder="$t('el.select.placeholder')"
            :filter-method="defaultFilterMethod"
            :disabled="onlyDisplay"
            :options="metaIndustryList"
            :props="metaIndustryCascaderProps"
          ></el-cascader>
        </div>
      </div>
      <div class="condition work-type">
        <div class="condition-label">
          {{ $t(`${i18nPrefix}.label.talentRecommendation.jobFunction`) }}
        </div>
        <div class="condition-controller">
          <RemoteLoadSelect
            v-model="condition.jobFunction"
            ref="jobFunction"
            labelKey="i18nName"
            clearable
            multiple
            :disabled="onlyDisplay"
            :loader="loadJobFunction"
          ></RemoteLoadSelect>
        </div>
      </div>
      <div class="condition job-title">
        <div class="condition-label">
          {{ $t(`${i18nPrefix}.label.lastWorkJobName`) }}
        </div>
        <div class="condition-controller">
          <RemoteLoadSelect
            v-model="condition.jobTitle"
            ref="jobTitle"
            labelKey="i18nName"
            clearable
            multiple
            default-first-option
            allow-create
            :disabled="onlyDisplay"
            :loader="loadJobTitle"
          ></RemoteLoadSelect>
        </div>
      </div>
      <div class="condition major">
        <div class="condition-label">
          {{ $t(`${i18nPrefix}.label.major`) }}
        </div>
        <div class="condition-controller">
          <RemoteLoadSelect
            v-model="condition.major"
            ref="major"
            labelKey="i18nName"
            clearable
            :disabled="onlyDisplay"
            :loader="loadMajor"
          ></RemoteLoadSelect>
        </div>
      </div>
      <div class="condition seniority">
        <div class="condition-label">
          {{ $t(`${i18nPrefix}.label.talentRecommendation.seniority`) }}
        </div>
        <div class="condition-controller">
          <el-select
            v-model="condition.seniority"
            clearable
            filterable
            multiple
            :disabled="onlyDisplay"
          >
            <el-option
              v-for="(seniority, key) in SENIORITY"
              :key="key"
              :label="$t(`${i18nPrefix}.seniority.${key}`)"
              :value="seniority"
            ></el-option>
          </el-select>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import RemoteLoadSelect from '@/components/formEditor/remoteLoadSelect.vue'
import RangeSelect from '../../../../views/adminNew/applicantList/rangeSelect.vue'
import { DEGREE } from '@/constant/meta'
import { addI18nProperty, isEn } from '@/i18n'
import { RESUME_SDK_TAG_KEYS, RESUME_SDK_TAG_TYPES } from '@/constant/company'
import { AsyncTask } from '@/utils/common'
import { MUNICIPALITY_CITY } from '@/constant/application'

const ACTIVENESS = {
  HOT: 'HOT',
  ACTIVE: 'ACTIVE',
  WARM: 'WARM',
  COLD: 'COLD'
}

const SENIORITY = {
  INTERN: 'Intern',
  ENTRY_LEVEL: 'Entry Level',
  JUNIOR: 'Junior',
  MIDDLE_LEVEL: 'Mid-Level',
  SENIOR: 'Senior',
  LEADER: 'Lead',
  PRINCIPAL: 'Principal',
  MANAGER: 'Manager',
  DIRECTOR: 'Director',
  SENIOR_DIRECTOR: 'Senior Director',
  VP: 'Vice President (VP)',
  EVP: 'Executive Vice President (EVP)',
  CHIEF_OFFICER: 'Chief Officer (e.g., CTO, CFO)'
}

export default {
  components: {
    RangeSelect,
    RemoteLoadSelect
  },
  props: {
    jobId: {
      required: true
    },
    isTalentPool: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.applicantList',
      DEGREE,
      ACTIVENESS,
      SENIORITY,
      loading: false,
      onlyDisplay: false,
      originCondition: {
        hard: null,
        soft: null
      },
      originConditionIsData: false,
      condition: {
        location: [],
        workYear: [null, null],
        degree: [],
        activenessType: [],
        recentIndustry: [],
        tag: [],
        industry: [],
        jobFunction: [],
        jobTitle: [],
        major: null,
        seniority: []
      },

      locationIdMap: {},
      countryList: [],
      locationList: [],
      locationLoader: null,
      tagIdMap: {},
      tagList: [],
      tagLoader: null,
      industryIdMap: {},
      industryList: [],
      industryLoader: null,
      metaIndustryIdMap: {},
      metaIndustryList: [],
      metaIndustryLoader: null,
      jobFunctionIdMap: {},
      jobFunctionList: [],
      jobFunctionLoader: null,
      majorIdMap: {},
      majorList: [],
      majorLoader: null,
      jobTitleLoader: null,
      jobTitleIdMap: {},
      jobTitleList: [],
      locationCascaderProps: {
        multiple: true,
        checkStrictly: true,
        value: 'id',
        label: 'i18nName'
      },
      metaIndustryCascaderProps: {
        multiple: true,
        value: 'id',
        label: 'i18nName'
      },
      resetData: false
    }
  },
  computed: {
    hasParseResumeTagPermission () {
      return this.hasAnyPermission('PREMIUM_CRM_PARSE_RESUME_SDK_TAG')
    },
    workYearOptionList () {
      return _.chain(0).range(16).map(year => {
        return {
          label: year >= 15 ? '15+' : year,
          value: year
        }
      }).value()
    }
  },
  created () {
    this.initPage()
  },
  methods: {
    ...mapActions('application', ['getMajorList', 'getIndustryList']),
    ...mapActions('crm', ['searchTag']),
    ...mapActions('metaInfo', ['getMetaIndustryList', 'getMetaJobFunctionList']),
    ...mapActions('jobFit', ['createGenerateJobFitConditionTask', 'getJobFitConditionTask']),
    ...mapActions('worldLocation', ['getWorldLocationList']),
    initPage (reload) {
      if (reload || !this.locationLoader) {
        this.locationLoader = this.loadLocation()
      }
      if (reload || !this.tagLoader) {
        this.tagLoader = this.loadTag()
      }
      if (reload || !this.industryLoader) {
        this.industryLoader = this.loadIndustry()
      }
      if (reload || !this.metaIndustryLoader) {
        this.metaIndustryLoader = this.loadMetaIndustry()
      }
      if (reload || !this.jobFunctionLoader) {
        this.jobFunctionLoader = this.loadJobFunction()
      }
      if (reload || !this.majorLoader) {
        this.majorLoader = this.loadMajor()
      }
      return Promise.all([
        this.locationLoader,
        this.tagLoader,
        this.industryLoader,
        this.metaIndustryLoader,
        this.jobFunctionLoader,
        this.majorLoader
      ])
    },
    async _loadLocation () {
      const resp = await this.getWorldLocationList({
        exclude_type: 'region'
      })
      if (resp) {
        this.locationList = _.map(resp, location => {
          addI18nProperty(location, 'i18nName', 'cn_name', 'name')
          location.searchTextList = _.map([location.name, location.cn_name, location.pinyin_name], _.toLower)
          if (location.parent_id) {
            const locationInfo = _.find(resp, item => item.id === location.parent_id)
            location.parent = locationInfo
            location.searchTextList.push(locationInfo.name, locationInfo.cn_name, locationInfo.pinyin_name)
          }
          // 处理四个直辖市, 不出现在下拉列表中
          if (_.findKey(MUNICIPALITY_CITY, city => city === location.id)) {
            location.searchTextList = null
          }
          if (location.type_code === 'region') {
            location.searchTextList = null
          }

          const nameList = []
          if (location.parent) {
            this.getParentLocation(location, nameList)
          }
          const searchLabelList = [location.cn_name]
          if (location.parent) {
            this.getParentLocationCnName(location, searchLabelList)
          }
          location.searchLabelList = _.reverse(searchLabelList)
          location.locationName = _.chain([location.i18nName, ...nameList]).filter().join(' - ').value()
          this.locationIdMap[location.id] = location
          return location
        })
      }
    },
    async loadLocation () {
      if (!this.locationLoader) {
        this.locationLoader = this._loadLocation()
      }
      await this.locationLoader
      return this.locationList
    },
    getParentLocation (location, nameList) {
      const parent = location.parent
      if (parent) {
        if (parent.searchTextList) {
          nameList.push(parent.i18nName)
        }
        if (parent.parent_id) {
          this.getParentLocation(parent, nameList)
        }
      }
    },
    getParentLocationCnName (location, nameList) {
      const parent = location.parent
      if (parent) {
        nameList.push(parent.cn_name)
        if (parent.parent_id) {
          this.getParentLocationCnName(parent, nameList)
        }
      }
    },
    async _loadTag () {
      const params = {
        tag_category: 'system',
        tag_type: RESUME_SDK_TAG_TYPES.SKILLS,
        tag_key: _.join([RESUME_SDK_TAG_KEYS.JOB_SKILL, RESUME_SDK_TAG_KEYS.SOFT_SKILL], ',')
      }
      if (this.hasParseResumeTagPermission) {
        params.is_common_tag = 1
      } else {
        params.is_company_tag = 1
      }
      const resp = await this.searchTag(params)
      const tagIdMap = {}
      const tagList = []
      _.forEach(resp, tag => {
        addI18nProperty(tag, 'i18nName', 'cn_name', 'name')
        tag.searchTextList = [tag.name, tag.cn_name]
        tagIdMap[tag.id] = tag
        if (tag.key === RESUME_SDK_TAG_KEYS.JOB_SKILL) {
          tag.isHard = true
        } else {
          tag.isSoft = true
        }
        tagList.push(tag)
      })
      this.tagIdMap = tagIdMap
      this.tagList = this.orderDataList(tagList)
    },
    async loadTag () {
      if (!this.tagLoader) {
        this.tagLoader = this._loadTag()
      }
      await this.tagLoader
      return this.tagList
    },
    async loadMetaIndustry () {
      if (!this.metaIndustryLoader) {
        this.metaIndustryLoader = this.getMetaIndustryList().then(resp => {
          if (resp) {
            const industryIdMap = {}
            const industryList = []
            const childrenMap = {}
            _.forEach(resp, (industry) => {
              addI18nProperty(industry, 'i18nName', 'cn_name', 'name')
              industry.searchTextList = _.map([industry.name, industry.cn_name], _.toLower)
              industryIdMap[industry.id] = industry
              if (industry.parent_id) {
                const children = childrenMap[industry.parent_id]
                if (children) {
                  children.push(industry)
                } else {
                  childrenMap[industry.parent_id] = [industry]
                }
              } else {
                if (!childrenMap[industry.id]) {
                  childrenMap[industry.id] = []
                }
                industry.children = childrenMap[industry.id]
                industryList.push(industry)
              }
            })
            this.metaIndustryList = this.orderDataList(industryList)
            this.metaIndustryIdMap = industryIdMap
          }
        })
      }
      await this.metaIndustryLoader
      return this.metaIndustryList
    },
    async loadJobFunction () {
      if (!this.jobFunctionLoader) {
        this.jobFunctionLoader = this.getMetaJobFunctionList().then(resp => {
          if (resp) {
            const jobFunctionIdMap = {}
            const jobFunctionList = _.map(resp, jobFunction => {
              addI18nProperty(jobFunction, 'i18nName', 'cn_name', 'name')
              jobFunction.searchTextList = [jobFunction.name, jobFunction.cn_name]
              jobFunctionIdMap[jobFunction.id] = jobFunction
              return jobFunction
            })
            this.jobFunctionList = this.orderDataList(jobFunctionList)
            this.jobFunctionIdMap = jobFunctionIdMap
          }
        })
      }
      await this.jobFunctionLoader
      return this.jobFunctionList
    },
    async _loadMajor () {
      const resp = await this.getMajorList()
      if (resp) {
        const majorIdMap = {}
        const majorList = _.map(resp.majors, major => {
          addI18nProperty(major, 'i18nName', 'cn_name', 'name')
          major.searchTextList = [major.name, major.cn_name]
          majorIdMap[major.id] = major
          return major
        })
        this.majorList = this.orderDataList(majorList)
        this.majorIdMap = majorIdMap
      }
    },
    async loadMajor () {
      if (!this.majorLoader) {
        this.majorLoader = this._loadMajor()
      }
      await this.majorLoader
      return this.majorList
    },
    async _loadJobTitle () {
      const params = {
        tag_category: 'system',
        tag_type: RESUME_SDK_TAG_TYPES.WORK_EXPERIENCE,
        tag_key: RESUME_SDK_TAG_KEYS.POSITION
      }
      if (this.hasParseResumeTagPermission) {
        params.is_common_tag = 1
      } else {
        params.is_company_tag = 1
      }
      const resp = await this.searchTag(params)
      const jobTitleIdMap = {}
      const jobTitleList = []
      _.forEach(resp, tag => {
        addI18nProperty(tag, 'i18nName', 'cn_name', 'name')
        tag.searchTextList = [tag.name, tag.cn_name]
        jobTitleIdMap[tag.id] = tag
        jobTitleList.push(tag)
      })
      this.jobTitleIdMap = jobTitleIdMap
      this.jobTitleList = this.orderDataList(jobTitleList)
    },
    async loadJobTitle () {
      if (!this.jobTitleLoader) {
        this.jobTitleLoader = this._loadJobTitle()
      }
      await this.jobTitleLoader
      return this.jobTitleList
    },
    async _loadIndustry () {
      const resp = await this.getIndustryList()
      if (resp) {
        const industryIdMap = {}
        const industryList = _.map(resp.industry_list, industry => {
          addI18nProperty(industry, 'i18nName', 'cn_name', 'name')
          industry.searchTextList = _.map([industry.name, industry.cn_name], _.toLower)
          industryIdMap[industry.id] = industry
          return industry
        })
        this.industryList = this.orderDataList(industryList)
        this.industryIdMap = industryIdMap
      }
    },
    async loadIndustry () {
      if (!this.industryLoader) {
        this.industryLoader = this._loadIndustry()
      }
      await this.industryLoader
      return this.industryList
    },
    defaultFilterMethod (node, keyword) {
      if (!keyword) {
        return true
      }
      const lowerQuery = _.toLower(keyword)
      return _.chain(node.data.searchTextList)
        .some(text => text.includes(lowerQuery) || text.includes(lowerQuery))
        .value()
    },
    async resetCondition (generateNew) {
      this.loading = true
      this.resetData = true
      this.initPage()
      if (generateNew) {
        this.onlyDisplay = false
        this.generateConditionTask = new AsyncTask(() => {
          return this.createGenerateJobFitConditionTask(this.jobId)
        }, (taskId) => {
          return this.getJobFitConditionTask({ jobId: this.jobId, taskId })
        }, (taskId, resp) => {
          if (!resp?.result) {
            this.loading = false
            this.originCondition = {}
            this.$message.error(this.$t(`${this.i18nPrefix}.message.talentRecommendConditionGenerateFailed`))
            return
          }
          this.originCondition = {
            soft: resp.result.soft,
            hard: resp.result.hard
          }
          this.resetCondition(false)
        })
      } else {
        // displayCondition 是保存到 DB 的数据结构与 createGenerateJobFitConditionTask 返回的数据结构不同
        const originCondition = this.originCondition
        const hard = originCondition.hard || {}
        const soft = originCondition.soft || {}

        while (!this.$refs.major) {
          await this.$nextTick()
        }
        const defaultValueKey = 'id'
        const skill = soft.skill

        this.$refs.recentIndustry.setDefaultValue(hard.recent_industry, defaultValueKey)
        this.$refs.tag.setDefaultValue(_.concat(skill?.hard, skill?.soft), defaultValueKey)
        this.$refs.jobFunction.setDefaultValue(soft.job_function, defaultValueKey)
        this.$refs.major.setDefaultValue(soft.major, defaultValueKey)
        this.$refs.jobTitle.setDefaultValue(soft.job_title, 'cn_name')

        const location = _.map(hard.location, location => location.city_id || location.province_id || location.country_id)
        this.$refs.location.setDefaultValue(location, defaultValueKey)

        await this.loadMetaIndustry()
        const metaIndustry = _.map(soft.industry, industryId => [this.metaIndustryIdMap[industryId].parent_id, industryId])

        _.assign(this.condition, {
          location,
          workYear: [hard.work_year_min, hard.work_year_max],
          industry: metaIndustry,
          degree: hard.degree,
          activenessType: this.isTalentPool ? (hard.activeness_type || []) : [],
          jobTitle: soft.job_title,
          seniority: soft.seniority || []
        })

        await this.$nextTick()
        this.loading = false
        await this.$nextTick()
        this.resetData = false
      }
    },
    async setConditionData (conditionData, isOnlyDisplay) {
      this.originCondition = conditionData
      this.onlyDisplay = isOnlyDisplay
      if (!conditionData?.hard || !conditionData?.soft) {
        return this.resetCondition(true)
      }
      return this.resetCondition(false)
    },
    async getConditionData () {
      if (this.onlyDisplay) {
        return
      }
      const condition = this.condition
      const hardTagIds = []
      const softTagIds = []
      _.forEach(condition.tag, tagId => {
        const tag = this.tagIdMap[tagId]
        if (tag.isHard) {
          hardTagIds.push(tagId)
        } else if (tag.isSoft) {
          softTagIds.push(tagId)
        }
      })
      const data = {
        hard: {
          location: _.chain(condition.location).map(locationId => {
            const location = this.locationIdMap[locationId]
            return this.formatLocation(location)
          }).value(),
          work_year_min: condition.workYear && condition.workYear[0],
          work_year_max: condition.workYear && condition.workYear[1],
          degree: _.orderBy(condition.degree),
          activeness_type: this.isTalentPool ? _.orderBy(condition.activenessType) : null,
          recent_industry: _.orderBy(condition.recentIndustry)
        },
        soft: {
          skill: {
            hard: _.orderBy(hardTagIds),
            soft: _.orderBy(softTagIds)
          },
          industry: _.chain(condition.industry).map(industryIds => industryIds[1]).orderBy().value(),
          job_function: _.orderBy(condition.jobFunction),
          job_title: _.map(condition.jobTitle, id => {
            const jobTitle = this.jobTitleIdMap[id]
            if (jobTitle) {
              return jobTitle.cn_name
            }
            return id
          }),
          major: condition.major,
          seniority: condition.seniority
        }
      }
      return data
    },
    orderDataList (itemList) {
      if (isEn()) {
        return _.orderBy(itemList, ['name'], ['asc'])
      } else {
        return itemList.sort((a, b) => {
          return a.cn_name.localeCompare(b.cn_name)
        })
      }
    },
    formatLocation (location, result) {
      if (!result) {
        result = {
          country_id: null,
          province_id: null,
          city_id: null
        }
      }
      if (!location || !location.id) {
        return result
      }
      if (!location.parent) {
        result.country_id = location.id
        return result
      }
      if (!location.parent.parent) {
        result.province_id = location.id
        return this.formatLocation(location.parent, result)
      }
      result.city_id = location.id
      return this.formatLocation(location.parent, result)
    }
  },
  watch: {
    condition: {
      deep: true,
      handler () {
        if (!this.resetData) {
          this.$emit('change')
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.job-condition-container {
  .condition-operator {
    margin: 10px 0;
  }
  .condition-container {
    word-break: break-word;
    .condition {
      display: flex;
      align-items: center;
      margin-bottom: 20px;
      .condition-label {
        width: 190px;
        .label {
          vertical-align: middle;
        }
        .tip-icon {
          color: #606266;
          margin-left: 4px;
          font-size: 14px;
          vertical-align: middle;
        }
      }
      .condition-controller {
        flex: 1;
        margin-left: 20px;
        .el-cascader {
          width: 100%;
          line-height: 28px;
          ::v-deep {
            .el-cascader__search-input {
              margin: 0 0 0 15px;
            }
            .el-cascader__tags .el-tag .el-icon-close {
              color: #909399;
              top: 0;
              &:hover {
                color: #ffffff;
              }
            }
          }
        }
      }
    }
  }
}
</style>
